import { useEffect, useState } from 'react';
import { MdContentCopy, MdCheck, MdIosShare, MdQrCode2, MdDownload } from 'react-icons/md';
import { useToast } from '@podus/ui';
import { canNativeShare, copyToClipboard, downloadDataUrl, generateQrDataUrl, nativeShare } from '@/lib/share';
import { referralLinkFor } from '@/lib/referral';

export interface ReferralLinkCardProps {
    code: string;
}

/**
 * The single most important thing on the ambassador dashboard (FR-22): the
 * link, one-click copy, native share on mobile, and a downloadable QR code for
 * print and in-person sharing.
 *
 * Takes only the code and builds the URL itself, so the copied link, the QR
 * code and the on-screen text can never disagree — or point at an environment
 * this build isn't talking to.
 */
export default function ReferralLinkCard({ code }: ReferralLinkCardProps) {
    const toast = useToast();
    const link = referralLinkFor(code);
    const [copied, setCopied] = useState(false);
    const [showQr, setShowQr] = useState(false);
    const [qr, setQr] = useState<string | null>(null);

    // Generate lazily — most sessions never open the QR panel.
    useEffect(() => {
        if (!showQr || qr) return;
        let cancelled = false;
        generateQrDataUrl(link)
            .then((dataUrl) => {
                if (!cancelled) setQr(dataUrl);
            })
            .catch(() => toast.error('Could not generate the QR code'));
        return () => {
            cancelled = true;
        };
    }, [showQr, qr, link, toast]);

    // Reset the copy affordance when the underlying link changes.
    useEffect(() => {
        setCopied(false);
        setQr(null);
    }, [link]);

    const handleCopy = async () => {
        const ok = await copyToClipboard(link);
        if (!ok) {
            toast.error('Could not copy — please copy the link manually');
            return;
        }
        setCopied(true);
        toast.success('Referral link copied');
        window.setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="rounded-2xl border border-[#E0E0E0] bg-white p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="text-[13px] text-[#747474]">Your referral link</p>
                    <p className="mt-0.5 text-[15px] font-bold text-secondary">
                        Code: <span className="text-primary">{code}</span>
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setShowQr((open) => !open)}
                        aria-expanded={showQr}
                        className="flex items-center gap-1.5 rounded-lg border border-[#E0E0E0] px-3 py-2 text-[13px] font-medium text-secondary transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdQrCode2 size={18} />
                        QR code
                    </button>

                    {canNativeShare() && (
                        <button
                            type="button"
                            onClick={() =>
                                nativeShare('Join me on Podus AI', 'Get instant legal help with Podus AI', link)
                            }
                            className="flex items-center gap-1.5 rounded-lg border border-[#E0E0E0] px-3 py-2 text-[13px] font-medium text-secondary transition-colors hover:bg-[#f5f5f5]"
                        >
                            <MdIosShare size={18} />
                            Share
                        </button>
                    )}
                </div>
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <div className="flex min-w-0 flex-1 items-center rounded-lg bg-[#f5f5f5] px-4 py-3">
                    <span className="truncate font-mono text-[14px] text-secondary" title={link}>
                        {link}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={handleCopy}
                    className="flex shrink-0 items-center justify-center gap-2 rounded-lg bg-secondary px-5 py-3 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                >
                    {copied ? <MdCheck size={18} /> : <MdContentCopy size={18} />}
                    {copied ? 'Copied' : 'Copy link'}
                </button>
            </div>

            {showQr && (
                <div className="animate-fade-in mt-5 flex flex-col items-center gap-4 rounded-xl border border-[#EEEEEE] bg-[#fafafa] p-5 sm:flex-row sm:items-center">
                    {qr ? (
                        <img src={qr} alt={`QR code for ${link}`} className="h-40 w-40 rounded-lg bg-white p-2" />
                    ) : (
                        <div className="skeleton-shimmer h-40 w-40 rounded-lg" />
                    )}
                    <div className="text-center sm:text-left">
                        <p className="text-[14px] font-bold text-secondary">Share offline</p>
                        <p className="mt-1 max-w-sm text-[13px] leading-5 text-[#747474]">
                            Print this on a flyer or show it on your phone. Anyone who scans it lands on your referral
                            link.
                        </p>
                        <button
                            type="button"
                            disabled={!qr}
                            onClick={() => qr && downloadDataUrl(qr, `podus-referral-${code}.png`)}
                            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#E0E0E0] bg-white px-4 py-2 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-50"
                        >
                            <MdDownload size={18} />
                            Download PNG
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
